import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import SearchableOptionList from "./SearchableOptionList";
import SavedSearchEditSubmenu from "./SavedSearchEditSubmenu";
import { panelLabelSx } from "./filterPanelStyles";
import {
  ALL_COMPANIES_PIN_ID,
  getSavedSearchPinId,
  isSavedSearchPinned,
  sortSavedSearchesByPin,
} from "../../../helper/pinnedSavedSearches";

export const ALL_COMPANIES_OPTION = {
  __allCompanies: true,
  id: ALL_COMPANIES_PIN_ID,
  name: "All companies",
};

const stopRowActivation = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

/**
 * Location-panel-style picker for V3/V4 custom views / saved searches.
 * V4 uses pin + edit row actions; V3 keeps delete.
 */
export default function SavedSearchPickerPanel({
  savedSearchChoices = [],
  selectedSavedSearch = null,
  onSelect,
  onDeleteSavedSearch,
  onPinSavedSearch,
  onRenameSavedSearch,
  pinnedIds = [],
  /** "delete" | "pin-edit" | "none" */
  rowActions = "delete",
  /** Close the parent Saved Search selector (after submenu actions). */
  onRequestClosePicker,
}) {
  const theme = useTheme();
  const [editMenuAnchor, setEditMenuAnchor] = useState(null);
  const [editMenuSearch, setEditMenuSearch] = useState(null);
  const editMenuOpen = Boolean(editMenuAnchor && editMenuSearch);

  const selectedValue = selectedSavedSearch || ALL_COMPANIES_OPTION;

  const options = useMemo(() => {
    const valid = (savedSearchChoices || []).filter((option) => option && option.name);
    if (rowActions === "pin-edit") {
      return sortSavedSearchesByPin(valid, pinnedIds);
    }
    return valid;
  }, [pinnedIds, rowActions, savedSearchChoices]);

  const closeEditMenu = () => {
    setEditMenuAnchor(null);
    setEditMenuSearch(null);
  };

  // Esc closes the edit submenu first; capture so the parent picker stays open.
  useEffect(() => {
    if (!editMenuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      closeEditMenu();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [editMenuOpen]);

  const openEditMenu = (event, option) => {
    stopRowActivation(event);
    setEditMenuAnchor(event.currentTarget);
    setEditMenuSearch(option);
  };

  const finishSubmenuAction = () => {
    closeEditMenu();
    onRequestClosePicker?.();
  };

  const handleRename = (option, event) => {
    stopRowActivation(event || { preventDefault() {}, stopPropagation() {} });
    finishSubmenuAction();
    if (typeof onRenameSavedSearch === "function") {
      onRenameSavedSearch(event, option);
      return;
    }
  };

  const handleDelete = (option, event) => {
    stopRowActivation(event || { preventDefault() {}, stopPropagation() {} });
    finishSubmenuAction();
    onDeleteSavedSearch?.(event || { stopPropagation() {} }, option);
  };

  return (
    <Box data-testid="saved-search-picker-panel">
      <Typography component="p" sx={{ ...panelLabelSx(theme), mb: 0.5 }}>
        Saved search
      </Typography>
      <SearchableOptionList
        options={options}
        leadingOptions={[ALL_COMPANIES_OPTION]}
        value={selectedValue}
        selectOption
        showSelectionControl={false}
        placeholder="Search saved searches..."
        emptyLabel="No saved searches match"
        getOptionLabel={(option) => option?.name || ""}
        getOptionKey={(option) =>
          option?.__allCompanies
            ? "__all-companies__"
            : option?._id || option?.id || option?.name || ""
        }
        onChange={(next) => {
          if (!next || next.__allCompanies) {
            onSelect?.(null);
            return;
          }
          onSelect?.(next);
        }}
        renderOptionEnd={(option) => {
          if (rowActions === "none") return null;

          if (rowActions === "pin-edit") {
            const optionId = getSavedSearchPinId(option);
            const pinned = isSavedSearchPinned(optionId, pinnedIds);
            const isAllCompanies = Boolean(option?.__allCompanies);
            return (
              <Box
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
                onClick={stopRowActivation}
                onMouseDown={stopRowActivation}
              >
                <Tooltip
                  title={
                    pinned
                      ? "Clear default search"
                      : isAllCompanies
                        ? "Set All companies as default"
                        : "Set as default search"
                  }
                >
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label={
                      pinned
                        ? `Clear default ${option.name}`
                        : `Set ${option.name} as default`
                    }
                    aria-pressed={pinned}
                    data-testid={`pin-saved-search-${optionId}`}
                    onClick={(event) => {
                      stopRowActivation(event);
                      onPinSavedSearch?.(event, option);
                    }}
                    sx={{
                      color: pinned ? theme.palette.primary.main : theme.palette.action.disabled,
                      "&:hover": {
                        color: theme.palette.primary.main,
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    {pinned ? (
                      <PushPinIcon fontSize="small" />
                    ) : (
                      <PushPinOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                {isAllCompanies ? null : (
                  <Tooltip title="Rename or delete saved search">
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label={`Rename or delete ${option.name}`}
                      data-testid={`edit-saved-search-${optionId}`}
                      aria-haspopup="menu"
                      aria-expanded={
                        editMenuSearch &&
                        (editMenuSearch.id || editMenuSearch._id) === optionId
                          ? "true"
                          : undefined
                      }
                      onClick={(event) => openEditMenu(event, option)}
                      sx={{
                        color: theme.palette.action.disabled,
                        "&:hover": {
                          color: theme.palette.text.primary,
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          }

          if (option?.__allCompanies || !onDeleteSavedSearch) return null;
          return (
            <IconButton
              edge="end"
              aria-label={`Delete ${option.name}`}
              size="small"
              onClick={(event) => {
                stopRowActivation(event);
                onDeleteSavedSearch(event, option);
              }}
              onMouseDown={stopRowActivation}
              sx={{
                color: theme.palette.action.disabled,
                "&:hover": {
                  color: theme.palette.error.main,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          );
        }}
      />

      <SavedSearchEditSubmenu
        anchorEl={editMenuAnchor}
        open={Boolean(editMenuAnchor && editMenuSearch)}
        savedSearch={editMenuSearch}
        onClose={(event, reason) => {
          closeEditMenu();
          if (reason === "backdropClick") {
            onRequestClosePicker?.();
          }
        }}
        onRename={handleRename}
        onDelete={handleDelete}
      />
    </Box>
  );
}
